import {cond, constant, eq, every, includes, isEmpty, size, some} from "lodash-es";
import {PredictionResult, UnitStatusType} from "modules/enums";
import type {getMatchByIdSelector} from "modules/selectors/units";

export enum DayStatus {
	MAKE_A_PICK = "MAKE_A_PICK",
	PICKED = "PICKED",
	ENDS = "ENDS",
	MULLIGAN = "MULLIGAN",
	CONTINUES = "CONTINUES",
	STREAK_ONE = "STREAK_ONE",
	STREAK_TWO = "STREAK_TWO",
	LOCKED = "LOCKED",
}

export const buildStreakStatus = (
	predictions: {unitId: number; result: PredictionResult | null}[],
	matchSelector: ReturnType<typeof getMatchByIdSelector>
) => {
	const isMatchesComplete = every(predictions, (pick) => {
		const match = matchSelector(pick.unitId);
		return match?.status === UnitStatusType.COMPLETE;
	});

	return cond([
		[isEmpty, constant(DayStatus.MAKE_A_PICK)],
		[
			(items: typeof predictions) =>
				every(items, {
					result: PredictionResult.VOID,
				}),
			constant(DayStatus.CONTINUES),
		],
		[
			(items: typeof predictions) =>
				some(items, {
					result: PredictionResult.USED_MULLIGAN,
				}),
			constant(DayStatus.MULLIGAN),
		],
		[
			(items: typeof predictions) =>
				isMatchesComplete &&
				some(items, {
					result: PredictionResult.NOT_HIT,
				}),
			constant(DayStatus.ENDS),
		],
		[
			(items: typeof predictions) =>
				every(items, {
					result: PredictionResult.HIT,
				}) && eq(size(items), 2),
			constant(DayStatus.STREAK_TWO),
		],
		[
			(items: typeof predictions) =>
				isMatchesComplete &&
				some(items, {
					result: PredictionResult.HIT,
				}),
			constant(DayStatus.STREAK_ONE),
		],
		[
			(items: typeof predictions) =>
				some(items, (pick) => {
					const match = matchSelector(pick.unitId);
					return includes(
						[UnitStatusType.COMPLETE, UnitStatusType.PLAYING],
						match?.status
					);
				}),
			constant(DayStatus.LOCKED),
		],
		[Boolean, constant(DayStatus.PICKED)],
	])(predictions);
};
